/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { Grid, InlineNotification, PageGridContainer, breakpoints } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import InformationBanner from 'bundles/onboarding-common/components/InformationBanner';
import useGetDefaultInferredRole from 'bundles/onboarding-common/hooks/useGetDefaultInferredRole';
import useGetOnboardingModuleConfig from 'bundles/onboarding-common/hooks/useGetOnboardingModuleConfig';
import GenericOnboardingModuleRenderer from 'bundles/onboarding-common/module-renderers/GenericOnboardingModuleRenderer';
import InferredRoleModuleRenderer from 'bundles/onboarding-common/module-renderers/InferredRoleModuleRenderer';
import ModularOnboardingProvider from 'bundles/onboarding-common/providers/ModularOnboardingProvider';
import type { InferredRole, ModularOnboardingEntrypoint } from 'bundles/onboarding-common/types';
import { getShouldShowModularOnboarding } from 'bundles/onboarding-common/utils/experimentUtils';
import { mapQuestionPathToQuestionIdList } from 'bundles/onboarding-common/utils/modularOnboarding';
import showDefaultInferredRole from 'bundles/onboarding-common/utils/showDefaultInferredRole';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/onboarding-common';

export type SuccessMessageProps = {
  headerText: string;
  bodyText: string;
  ctaButton: {
    text: string;
    href?: string;
  };
};

type Props = {
  entrypoint: ModularOnboardingEntrypoint;
  inferredRole?: InferredRole;
  shouldShowSuccessMessageOnCompletion?: boolean;
  successMessage?: SuccessMessageProps;
  onOnboardingCompletionSuccess?: () => void;
  className?: string;
};

const styles = {
  container: css`
    margin: var(--cds-spacing-300) auto;
    width: 100%;
  `,
  placeholder: css`
    height: 100px;
    border-radius: var(--cds-spacing-200);
    border: 2px solid var(--cds-color-grey-50);
    background: var(--cds-color-emphasis-primary-background-xweak);
    width: 100%;

    ${breakpoints.down('sm')} {
      height: 144px;
    }
  `,
};

const ModularOnboardingModuleRenderer = ({
  entrypoint,
  inferredRole,
  shouldShowSuccessMessageOnCompletion,
  successMessage,
  onOnboardingCompletionSuccess,
  className,
}: Props) => {
  const skipDefaultInferredRoleQuery = !isAuthenticatedUser() || !!inferredRole || !showDefaultInferredRole();
  const { inferredRole: defaultInferredRole, inferredRoleLoading: defaultInferredRoleLoading } =
    useGetDefaultInferredRole(skipDefaultInferredRoleQuery);
  const skipOnboardingModuleConfigQuery = !isAuthenticatedUser() || (!inferredRole && defaultInferredRoleLoading);
  const { displayModule, module } = useGetOnboardingModuleConfig(
    {
      entrypoint,
      inferredRole: inferredRole ?? defaultInferredRole,
    },
    skipOnboardingModuleConfigQuery
  );
  const [isSuccessBannerDismissed, setIsSuccessBannerDismissed] = useState(false);
  const [isModuleDismissed, setIsModuleDismissed] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleSuccessBannerDismissal = () => {
    setIsSuccessBannerDismissed(true);
  };

  const handleOnboardingCompletionSuccess = () => {
    if (shouldShowSuccessMessageOnCompletion) {
      setShowSuccessMessage(true);
    } else if (onOnboardingCompletionSuccess) {
      onOnboardingCompletionSuccess();
    } else {
      redirectClientOrServer('/?completedOnboarding=true');
    }
  };

  const isContentDismissed = isModuleDismissed || isSuccessBannerDismissed;

  if (!displayModule || !isAuthenticatedUser() || !getShouldShowModularOnboarding() || isContentDismissed) {
    return null;
  }

  const renderSuccessMessage = () => {
    const { headerText, bodyText, ctaButton } = successMessage ?? {};

    return (
      <InformationBanner
        headerText={headerText ?? _t('Your goals have been set')}
        bodyText={bodyText ?? _t('Your personalized recommendations are ready, designed to help you reach your goals.')}
        textLayout="separate-lines"
        isDismissed={isSuccessBannerDismissed}
        onDismiss={handleSuccessBannerDismissal}
        ctaButton={{
          label: ctaButton?.text ?? _t('View my recommendations'),
          href: ctaButton?.href ?? '/?completedOnboarding=true',
        }}
      />
    );
  };

  const renderOnboardingModule = () => {
    switch (module?.__typename) {
      case 'GenericModule': {
        return (
          <ModularOnboardingProvider questionIds={mapQuestionPathToQuestionIdList(module.questionPath)}>
            <GenericOnboardingModuleRenderer
              onOnboardingCompletionSuccess={handleOnboardingCompletionSuccess}
              onDismiss={() => setIsModuleDismissed(true)}
            />
          </ModularOnboardingProvider>
        );
      }
      case 'InferredRoleModule': {
        const { inferredRole: inferredRoleFromModule } = module;
        return (
          <InferredRoleModuleRenderer
            inferredRole={inferredRoleFromModule}
            onConfirmRoleSuccess={handleOnboardingCompletionSuccess}
            onDismiss={() => setIsModuleDismissed(true)}
          />
        );
      }

      default:
        return null;
    }
  };

  const renderContent = () => {
    if (showSuccessMessage) {
      return renderSuccessMessage();
    }
    return renderOnboardingModule();
  };

  const renderPlaceholder = () => {
    return <div css={styles.placeholder} />;
  };

  return (
    <ErrorBoundaryWithTagging
      componentName="ModularOnboardingModuleRenderer"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error with the modular onboarding component. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <div className={className} css={styles.container}>
        <PageGridContainer>
          <Grid item xs={12}>
            {inServerContext && !inferredRole ? renderPlaceholder() : renderContent()}
          </Grid>
        </PageGridContainer>
      </div>
    </ErrorBoundaryWithTagging>
  );
};

export default ModularOnboardingModuleRenderer;
